import { TextCell } from "@similarweb/pro-logic-components";
import type { AnyObject, ColumnsType, ColumnType } from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";

import { TrafficShare } from "app/components/React/Table/cells";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { NO_CELL, PREVIEW_CELL_SPAN, PreviewCell } from "../shared/TableComponents";
import type { AdIntelligenceController } from "../shared/types/types";
import { NA_VALUE, obfuscateData } from "../utils/utils";

export const getColumns = (
    isPdf: string,
    hasAdIntelMetrics: boolean,
    isPreview: boolean,
    handleSeeAll: () => void,
    rowCount: number,
    resource: AdIntelligenceController,
    hasAdIntelBeta: boolean,
): ColumnsType<AnyObject> => {
    const isPdfBoolean = isPdf === "true";

    const getWebsiteLink = (url: string) => {
        if (resource === "publisher") {
            return publisherAnalysisNavigation.overview.getLink({ ...proNavigator.getParams(), key: url });
        }
        return displayAdvertisingNavigation.overview.getLink({ ...proNavigator.getParams(), key: url });
    };

    const websiteColumn = {
        title: () => translate(`ad.intelligence.overview.top.${resource}s.column.title`),
        dataIndex: resource,
        width: isPdfBoolean ? 220 : 232,
        render: ({ url, favicon }: Record<string, string> = {}) =>
            url ? (
                <CoreWebsiteCell
                    domain={url}
                    icon={favicon}
                    textAndIconMargin="8px"
                    showToolTip={!isPreview}
                    internalLink={getWebsiteLink(url)}
                    trackInternalLink={(e) => {
                        e.stopPropagation();
                        allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${url}`);
                    }}
                    childrenCount={0}
                    spacingDomainAndAddons={false}
                    TooltipComponent={({ children }) => (
                        <Tooltip
                            content={translate("ad.intelligence.overview.publishers.table.tooltip", { site: url })}
                            placement="top"
                        >
                            {children}
                        </Tooltip>
                    )}
                />
            ) : null,
    } satisfies ColumnType<AnyObject>;

    const spendColumn = {
        title: translate("ad.intelligence.publishers.spend.column.title"),
        align: "left" as const,
        dataIndex: "spend",
        resize: false,
        onCell: isPreview ? (_, index) => (index === 0 ? PREVIEW_CELL_SPAN : NO_CELL) : undefined,
        render: (
            spend: { total: number; value: number },
            row: { visits?: { total: undefined; value: number } } = {},
            index: number,
        ) => {
            if (isPreview && index === 0) {
                return (
                    <PreviewCell
                        onButtonClick={handleSeeAll}
                        buttonText={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.button`)}
                        title={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.title`)}
                        subtitle={translate(`ad.intelligence.overview.${resource}s.table.preview.cell.subtitle`)}
                        rowCount={rowCount}
                    />
                );
            }
            if (isPreview) {
                return null;
            }

            const { change } = obfuscateData(spend.total, spend.value, hasAdIntelMetrics, row.visits?.value ?? 0);

            if (!change || change === 0) {
                return <TextCell>{NA_VALUE}</TextCell>;
            }

            return (
                <TrafficShare
                    value={change}
                    row={row}
                    undefinedSign={NA_VALUE}
                />
            );
        },
    } satisfies ColumnType<AnyObject>;

    const impressionsColumn = {
        title: translate("ad.intelligence.publishers.impressions.column.title"),
        align: "left" as const,
        dataIndex: "impressions",
        resize: false,
        onCell: isPreview ? () => NO_CELL : undefined,
        render: (
            impressions: { total: number; value: number },
            row: { visits?: { total: undefined; value: number } } = {},
        ) => {
            if (isPreview) {
                return null;
            }

            const { change } = obfuscateData(
                impressions.total,
                impressions.value,
                hasAdIntelMetrics,
                row.visits?.value ?? 0,
            );

            if (!change || change === 0) {
                return <TextCell>{NA_VALUE}</TextCell>;
            }

            return (
                <TrafficShare
                    value={change}
                    row={row}
                    undefinedSign={NA_VALUE}
                />
            );
        },
    } satisfies ColumnType<AnyObject>;

    const visitsColumn = {
        title: translate("ad.intelligence.publishers.visits.column.title"),
        align: "left" as const,
        dataIndex: "visits",
        render: (visits: { total: undefined; value: number }, row: unknown) => {
            // for padded data rows, we don't have visits
            if (isPreview && !visits?.value) {
                return null;
            }

            if (!visits?.value || visits.value === 0) {
                return <TextCell>{NA_VALUE}</TextCell>;
            }

            return (
                <TrafficShare
                    value={visits.value}
                    row={row}
                    undefinedSign={NO_DATA_SIGN}
                />
            );
        },
    } satisfies ColumnType<AnyObject>;

    if (isPreview && hasAdIntelBeta) {
        return [websiteColumn, visitsColumn];
    }

    if (isPreview) {
        return [websiteColumn, visitsColumn, spendColumn, impressionsColumn];
    }

    return [websiteColumn, spendColumn, impressionsColumn, visitsColumn];
};
